/* Debounced auto-save.
 *
 *   const { status, error, saveNow } = useAutoSave({ dirty, save, signature });
 *
 * Watches `dirty`; while it's true, schedules `save()` after `delay` ms of
 * quiet. Each edit (a change in `signature`) resets the timer, so we only
 * persist once the user pauses. A pending save is flushed on unmount, so the
 * last edit isn't lost when a modal or inline editor closes a beat after the
 * final keystroke.
 *
 * `save` must persist current state and reset its own baseline so `dirty`
 * flips false afterwards. It must NOT tear down the container (close a modal
 * / collapse an editor) — that stays an explicit user action, otherwise the
 * UI would vanish mid-type.
 *
 * Returns:
 *   status   'idle' | 'saving' | 'saved' | 'error'
 *   error    last error message (string) or null
 *   saveNow  flush any pending save immediately (e.g. on blur)
 */
import React from 'react';

export function useAutoSave({ dirty, save, signature, delay = 900, enabled = true }) {
  const [status, setStatus] = React.useState('idle');
  const [error, setError] = React.useState(null);

  // Refs keep the latest values visible to timers / unmount cleanup without
  // re-subscribing effects on every render.
  const saveRef = React.useRef(save);
  saveRef.current = save;
  const dirtyRef = React.useRef(dirty);
  dirtyRef.current = dirty;
  const enabledRef = React.useRef(enabled);
  enabledRef.current = enabled;

  const runningRef = React.useRef(false); // a save is in flight
  const pendingRef = React.useRef(false); // an edit landed during that save
  const timerRef = React.useRef(null);

  const run = React.useCallback(async () => {
    if (!enabledRef.current || !dirtyRef.current) return;
    if (runningRef.current) { pendingRef.current = true; return; }
    runningRef.current = true;
    pendingRef.current = false;
    setStatus('saving');
    setError(null);
    try {
      await saveRef.current();
      setStatus('saved');
    } catch (err) {
      setError(err?.message || String(err));
      setStatus('error');
    } finally {
      runningRef.current = false;
      // Edits arrived mid-save — persist them too.
      if (pendingRef.current && dirtyRef.current && enabledRef.current) {
        pendingRef.current = false;
        run();
      }
    }
  }, []);

  React.useEffect(() => {
    if (!enabled || !dirty) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(run, delay);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [enabled, dirty, signature, delay, run]);

  // Flush a not-yet-fired save when the editor unmounts. Fire-and-forget:
  // the network request still goes out even though we can't update state.
  React.useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (enabledRef.current && dirtyRef.current && !runningRef.current) {
      try {
        const p = saveRef.current?.();
        if (p && typeof p.then === 'function') p.catch(() => {});
      } catch { /* best effort */ }
    }
  }, []);

  const saveNow = React.useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    run();
  }, [run]);

  return { status, error, saveNow };
}

/* Inline status pill that replaces the old "Save" button. Combines the
 * persisted `status` with the live `dirty` flag so the user always knows
 * whether their latest keystroke is on disk yet. */
export function SaveStatus({ status, error, dirty, style }) {
  let label;
  let color = 'var(--fg-muted)';
  if (status === 'error') {
    label = error ? `Save failed — ${error}` : 'Save failed';
    color = '#A20235';
  } else if (status === 'saving') {
    label = 'Saving…';
  } else if (dirty) {
    label = 'Editing…';
  } else if (status === 'saved') {
    label = 'Saved';
    color = 'var(--support-green)';
  } else {
    label = '';
  }
  if (!label) return null;
  return (
    <span
      role="status"
      aria-live="polite"
      style={{ fontSize: 11.5, color, fontStyle: status === 'error' ? 'normal' : 'italic', ...style }}
    >
      {status === 'saved' && !dirty ? '✓ ' : ''}{label}
    </span>
  );
}
